/**
 * Panneau des quêtes du jour et de la chaîne de connexion (LIVRAISON 2.1).
 *
 * C'est l'écran qui répond à « pourquoi rouvrir Villopia demain ». Il vit dans la
 * section **Progression** du dock, aux côtés des ligues et du prestige.
 *
 * Deux partis pris d'affichage.
 *
 * **Une quête terminée ne disparaît pas, elle devient encaissable.** Faire disparaître
 * la ligne priverait le joueur du moment qui compte — celui où il récolte. Le bouton
 * remplace la barre, au même endroit.
 *
 * **La chaîne de connexion est en tête, pas en bas.** C'est elle qui porte le coût de
 * ne pas revenir ; la reléguer sous les quêtes reviendrait à cacher l'argument.
 */

import { GEM_SPENDING } from '@/config/balance';
import { duration, n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import type { CitySnapshot } from '@/core/gameState';
import { canBuyStreakRepair, questCounters } from '@/core/gameState';
import {
  nextDailyResetAt,
  nextWeeklyResetAt,
  questProgress,
  streakReward,
  weeklyProgress,
} from '@/core/quests';
import type { QuestProgress } from '@/core/quests';
import { createButton, createPanel } from './components';
import { icon } from './icons';

export interface QuestPanelCallbacks {
  readonly onClaim: (questId: string) => void;
  readonly onClaimWeekly: (questId: string) => void;
  readonly onClaimStreak: () => void;
  /** Changer les trois quêtes du jour contre des gemmes (G§3, A6). Une fois par jour. */
  readonly onReroll?: () => void;
  /** Rétablir une chaîne rompue AUJOURD'HUI (G§6, F1). Une fois par semaine. */
  readonly onRepairStreak?: () => void;
}

export interface QuestPanelHandle {
  readonly el: HTMLElement;
  update(snapshot: CitySnapshot): void;
}

export function createQuestPanel(callbacks: QuestPanelCallbacks): QuestPanelHandle {
  const el = document.createElement('div');
  el.className = 'quest-panel';

  /* ── Chaîne de connexion ─────────────────────────────────────────────── */
  const streakDays = document.createElement('b');
  streakDays.className = 'quest-streak__days';

  const streakHint = document.createElement('span');
  streakHint.className = 'quest-streak__hint';

  const streakButton = createButton({
    label: t('quests.claim'),
    // Le montant est posé à l'affichage : il dépend de la longueur de la chaîne.
    variant: 'primary',
    icon: 'gem',
    onClick: callbacks.onClaimStreak,
  });

  const streakHead = document.createElement('div');
  streakHead.className = 'quest-streak__head';
  streakHead.append(icon('bolt', { size: 'large' }), streakDays, streakHint);

  /*
    ── LE RACHAT DE CHAÎNE N'APPARAÎT QUE LE JOUR DE LA RUPTURE ──────────────

    C'est le troisième moment de vente du G§7, et le seul qui porte une émotion : le
    joueur vient de perdre onze jours d'affilée. L'offre est donc à CÔTÉ du compteur
    qui vient de retomber à 1, pas dans un onglet qu'il faudrait penser à ouvrir.

    Elle est masquée le reste du temps — `hidden`, qui gagne toujours grâce à
    `[hidden] { display: none !important }` en tête de `components.css`. Une offre de
    réparation affichée en permanence dirait au joueur qu'on attend sa rupture.
  */
  const repairHint = document.createElement('span');
  repairHint.className = 'quest-streak__lost';

  const repairButton = createButton({
    label: t('gems.repairCost', { value: n(GEM_SPENDING.streakRepair.cost) }),
    variant: 'primary',
    icon: 'gem',
    onClick: () => {
      callbacks.onRepairStreak?.();
    },
  });
  repairButton.el.classList.add('quest-streak__repair');

  const repairRow = document.createElement('div');
  repairRow.className = 'quest-streak__repair-row';
  repairRow.hidden = true;
  repairRow.append(repairHint, repairButton.el);

  const streakPanel = createPanel({
    children: [streakHead, streakButton.el, repairRow],
  });
  streakPanel.el.classList.add('quest-streak');

  /*
    ── Quêtes du jour, ET leur échéance ─────────────────────────────────────

    Un objectif sans date est un objectif sans urgence. Le panneau disait quoi faire et
    jamais jusqu'à quand : une quête terminée ne donnait aucune raison de revenir CE
    SOIR, et une quête ratée se découvrait le lendemain sans qu'on ait pu la sauver.
    L'échéance est ce qui transforme une liste de tâches en rendez-vous.

    Le titre porte donc son compte à rebours, à droite. L'instant vient de `core`
    (`nextDailyResetAt`), inverse exact de la frontière de journée — le recalculer ici
    aurait fait diverger l'affichage de la logique au premier changement de convention.
  */
  const title = document.createElement('h3');
  title.className = 'quest-panel__title';

  const titleLabel = document.createElement('span');
  titleLabel.textContent = t('quests.daily');

  const dailyTimer = document.createElement('span');
  dailyTimer.className = 'quest-panel__timer';

  /*
    La relance est dans le TITRE du jour, pas sous la liste : c'est une action sur la
    sélection entière, et son prix se lit à côté du compte à rebours qui explique
    pourquoi on voudrait la changer.
  */
  const rerollButton = createButton({
    label: t('gems.rerollCost', { value: n(GEM_SPENDING.reroll.cost) }),
    variant: 'ghost',
    icon: 'rotate',
    onClick: () => {
      callbacks.onReroll?.();
    },
  });
  rerollButton.el.classList.add('quest-panel__reroll');

  /*
    ── PAS DANS LE TITRE : DANS SA PROPRE RANGÉE ─────────────────────────────

    Premier essai : le bouton était glissé dans le `<h3>`, à droite du compte à rebours.
    Mesuré à 390 px — sa cible tombait à 48 px de haut mais SOUS LE PLI du tiroir, et le
    joueur devait faire défiler pour découvrir une action dont il ne soupçonnait pas
    l'existence. Une offre qu'on ne voit pas n'existe pas (G§7).

    Elle a donc sa rangée, sous le titre, avec son libellé : le prix ET ce qu'il achète.
    Un bouton d'icône seul dans un titre n'aurait de sens que pour qui connaît déjà
    l'offre — c'est-à-dire personne, le jour de sa livraison.
  */
  const rerollLabel = document.createElement('span');
  rerollLabel.className = 'quest-reroll__label';
  rerollLabel.textContent = t('gems.reroll');

  const rerollRow = document.createElement('div');
  rerollRow.className = 'quest-reroll';
  rerollRow.append(rerollLabel, rerollButton.el);

  title.append(titleLabel, dailyTimer);

  const list = document.createElement('div');
  list.className = 'quest-list';

  /* ── Objectifs de la semaine ─────────────────────────────────────────────
     Second horizon : plus lointain, mieux récompensé. Placé SOUS le quotidien —
     ce qu'on peut finir aujourd'hui passe avant ce qui court jusqu'à dimanche. */
  const weeklyTitle = document.createElement('h3');
  weeklyTitle.className = 'quest-panel__title';

  const weeklyLabel = document.createElement('span');
  weeklyLabel.textContent = t('quests.weekly');

  const weeklyTimer = document.createElement('span');
  weeklyTimer.className = 'quest-panel__timer';

  weeklyTitle.append(weeklyLabel, weeklyTimer);

  const weeklyList = document.createElement('div');
  weeklyList.className = 'quest-list';

  el.append(streakPanel.el, title, rerollRow, list, weeklyTitle, weeklyList);

  /**
   * Empreinte de ce qui est affiché : on ne reconstruit que si elle change.
   *
   * **Sans cela, le bouton « Encaisser » ne fonctionnait pas.** `update` est appelé à
   * chaque changement d'état, soit dix fois par seconde puisque les pièces montent en
   * continu. Le bouton était donc détruit et recréé entre l'appui et le relâchement, et
   * le navigateur n'émettait jamais de `click` — l'événement exige que les deux se
   * produisent sur le MÊME élément.
   *
   * Le défaut était invisible en test automatisé, qui appuie et relâche en une frame,
   * et systématique pour un joueur réel, dont le doigt reste posé plus de 100 ms.
   */
  let signature = '';
  let weeklySignature = '';

  /**
   * Une seule fonction dessine les deux horizons.
   *
   * Les dupliquer aurait fait diverger leur rendu au premier ajustement — et c'est
   * exactement le genre d'écart qu'un joueur remarque avant nous.
   */
  function renderList(
    host: HTMLElement,
    entries: readonly QuestProgress[],
    claim: (id: string) => void,
  ): void {
    host.replaceChildren();

    for (const entry of entries) {
      const row = document.createElement('div');
      row.className = 'quest';
      if (entry.claimed) row.classList.add('is-claimed');

      const label = document.createElement('span');
      label.className = 'quest__label';
      label.textContent = t(entry.quest.labelKey as TranslationKey, {
        value: n(entry.quest.target),
      });

      const reward = document.createElement('span');
      reward.className = 'quest__reward';
      reward.append(icon('gem'), String(entry.quest.gems));

      const head = document.createElement('div');
      head.className = 'quest__head';
      head.append(label, reward);
      row.append(head);

      if (entry.claimed) {
        const done = document.createElement('span');
        done.className = 'quest__done';
        done.append(icon('check'), t('quests.claimed'));
        row.append(done);
      } else if (entry.complete) {
        const button = createButton({
          label: t('quests.claimFor', { value: String(entry.quest.gems) }),
          variant: 'primary',
          icon: 'gem',
          onClick: () => {
            claim(entry.quest.id);
          },
        });
        button.el.classList.add('quest__claim');
        row.append(button.el);
      } else {
        const fill = document.createElement('i');
        fill.style.width = `${(entry.ratio * 100).toFixed(1)}%`;

        const bar = document.createElement('div');
        bar.className = 'quest__bar';
        bar.append(fill);

        const count = document.createElement('span');
        count.className = 'quest__count';
        count.textContent = `${n(entry.current)} / ${n(entry.quest.target)}`;

        row.append(bar, count);
      }

      host.append(row);
    }
  }

  /** Met à jour les seules barres, sans toucher aux nœuds interactifs. */
  function refreshBars(host: HTMLElement, entries: readonly QuestProgress[]): void {
    const bars = host.querySelectorAll<HTMLElement>('.quest__bar i');
    const counts = host.querySelectorAll<HTMLElement>('.quest__count');
    entries.forEach((entry, index) => {
      const bar = bars[index];
      const count = counts[index];
      if (bar !== undefined) bar.style.width = `${(entry.ratio * 100).toFixed(1)}%`;
      if (count !== undefined) count.textContent = `${n(entry.current)} / ${n(entry.quest.target)}`;
    });
  }

  return {
    el,
    update(snapshot) {
      const counters = questCounters(snapshot);
      const state = snapshot.quests;

      /*
        Les deux échéances, à chaque passe.

        Aucune minuterie propre : `update` est déjà appelé à chaque changement d'état,
        donc plusieurs fois par seconde tant que la production tourne. Un `setInterval`
        de plus n'ajouterait qu'une source de désynchronisation — et un compte à rebours
        qui avance à un rythme différent du reste du HUD se remarque.
      */
      const now = Date.now();
      dailyTimer.textContent = t('quests.renewsIn', {
        value: duration(Math.max(0, nextDailyResetAt(now) - now)),
      });
      weeklyTimer.textContent = t('quests.renewsIn', {
        value: duration(Math.max(0, nextWeeklyResetAt(now) - now)),
      });

      streakDays.textContent = t('quests.streak', { value: String(state.streak) });

      /*
        La rupture se lit sur `lostStreakDay`, pas sur « streak === 1 » : un joueur qui
        démarre aujourd'hui a lui aussi une chaîne de 1, et lui proposer de racheter une
        chaîne qu'il n'a jamais eue serait une offre mensongère. `canBuyStreakRepair`
        porte les trois conditions (rupture du jour, chaîne d'au moins deux jours,
        plafond hebdomadaire) — l'écran les LIT, il ne les redevine pas.
      */
      const repairable = canBuyStreakRepair(snapshot, Date.now());
      repairRow.hidden = !repairable;
      if (repairable) {
        repairHint.textContent = t('gems.repairTitle', { value: n(state.lostStreak) });
        repairButton.setDisabled(snapshot.gems < GEM_SPENDING.streakRepair.cost);
      }

      // Relance : un seul refus possible ici — déjà relancé aujourd'hui. Le manque de
      // gemmes grise le bouton en gardant son prix, qui reste l'information utile.
      const rerolled = state.rerollDay === state.day;
      rerollButton.setDisabled(rerolled || snapshot.gems < GEM_SPENDING.reroll.cost);
      rerollButton.setLabel(
        rerolled
          ? t('gems.rerollDone')
          : t('gems.rerollCost', { value: n(GEM_SPENDING.reroll.cost) }),
      );

      const claimable = state.streakClaimedDay !== state.day;
      streakHint.textContent = claimable
        ? t('quests.streakReady', { value: n(streakReward(state.streak)) })
        : t('quests.streakDone');
      streakButton.setLabel(
        claimable
          ? t('quests.claimFor', { value: String(streakReward(state.streak)) })
          : t('quests.claim'),
      );
      streakButton.setDisabled(!claimable);

      const entries = questProgress(state, counters);

      /*
        Empreinte de la STRUCTURE, pas de la progression.

        Le panneau ne se reconstruit qu'à un changement réel — quête terminée,
        encaissée, nouvelle période. Sinon seules les barres bougent : reconstruire à
        chaque tick détruirait les boutons entre l'appui et le relâchement, et le clic
        se perdrait. C'est le défaut qui a coûté le plus cher au projet.
      */
      const daily = entries
        .map((e) => `${e.quest.id}:${String(e.complete)}:${String(e.claimed)}`)
        .join('|');

      if (daily === signature) {
        refreshBars(list, entries);
      } else {
        signature = daily;
        renderList(list, entries, callbacks.onClaim);
      }

      const weeklyEntries = weeklyProgress(snapshot.weekly, counters);
      const weekKey = weeklyEntries
        .map((e) => `${e.quest.id}:${String(e.complete)}:${String(e.claimed)}`)
        .join('|');

      if (weekKey === weeklySignature) {
        refreshBars(weeklyList, weeklyEntries);
      } else {
        weeklySignature = weekKey;
        renderList(weeklyList, weeklyEntries, callbacks.onClaimWeekly);
      }
    },
  };
}
